import React, { useEffect, useRef, useState } from 'react'
import { Typography, withStyles, Slider, Box, Tooltip } from '@material-ui/core'
import { Icon } from '@iconify/react'
import styles from './styles'
import clsx from 'clsx'
import { useDispatch } from 'react-redux'
import { aICreatorDeleteAttachmentAction } from 'src/reducers/ai-creator/action'
import { track } from '@amplitude/analytics-browser'
import { CoreButton } from 'src/components/core'
import {
  buildSuccessRampSteps,
  useDummyAttachmentProgress,
} from '../../hooks/useDummyAttachmentProgress'

const CourseCreationAttachmentView = props => {
  const {
    classes,
    attachments,
    handleOnDropFile,
    fileInputRef,
    handleFileSelect,
    attachmentLoading,
    documentFollowing,
    onDocumentFollowingChange,
    onAddSource,
  } = props
  const [hoveredIconId, setHoveredIconId] = useState(null)
  const [completionProgress, setCompletionProgress] = useState({})
  const [completionState, setCompletionState] = useState({})
  const completionProgressRef = useRef({})
  const completionTimersRef = useRef({})
  const completedAnimationsRef = useRef(new Set())
  const dispatch = useDispatch()
  const dummyProgress = useDummyAttachmentProgress(attachments)

  const validAttachments = attachments.filter(
    attachment =>
      attachment.upload_status !== 'partial_failure' &&
      attachment.upload_status !== 'failed' &&
      !(attachment.error_details && attachment.error_details.length > 0),
  )

  const clearCompletionTimers = attachmentId => {
    const attachmentKey = String(attachmentId)
    const timers = completionTimersRef.current[attachmentKey] || []
    timers.forEach(timerId => clearTimeout(timerId))
    delete completionTimersRef.current[attachmentKey]
  }

  useEffect(() => {
    completionProgressRef.current = completionProgress
  }, [completionProgress])

  useEffect(() => {
    const validIds = new Set(
      validAttachments.map(item => String(item.attachment_id)),
    )

    Object.keys(completionTimersRef.current).forEach(attachmentId => {
      if (!validIds.has(attachmentId)) {
        clearCompletionTimers(attachmentId)
      }
    })

    setCompletionProgress(prev => {
      const next = { ...prev }
      Object.keys(next).forEach(attachmentId => {
        if (!validIds.has(attachmentId)) {
          delete next[attachmentId]
        }
      })
      return next
    })

    setCompletionState(prev => {
      const next = { ...prev }
      Object.keys(next).forEach(attachmentId => {
        if (!validIds.has(attachmentId)) {
          delete next[attachmentId]
        }
      })
      return next
    })

    completedAnimationsRef.current.forEach(attachmentId => {
      if (!validIds.has(attachmentId)) {
        completedAnimationsRef.current.delete(attachmentId)
      }
    })

  }, [validAttachments])

  useEffect(() => {
    validAttachments.forEach(attachment => {
      const attachmentId = String(attachment.attachment_id)

      if (attachment.succeeded !== true) {
        return
      }

      if (completedAnimationsRef.current.has(attachmentId)) {
        return
      }

      if (completionTimersRef.current[attachmentId]) {
        return
      }

      completedAnimationsRef.current.add(attachmentId)

      const startProgress = Math.max(
        completionProgressRef.current[attachmentId] ??
          dummyProgress[attachmentId] ??
          0,
        1,
      )
      const rampSteps = buildSuccessRampSteps(startProgress)
      const timers = []
      completionTimersRef.current[attachmentId] = timers

      setCompletionState(prev => ({ ...prev, [attachmentId]: 'ramping' }))
      setCompletionProgress(prev => ({ ...prev, [attachmentId]: startProgress }))

      rampSteps.forEach((stepValue, stepIndex) => {
        const timerId = setTimeout(() => {
          setCompletionProgress(prev => ({ ...prev, [attachmentId]: stepValue }))
        }, (stepIndex + 1) * 90)
        timers.push(timerId)
      })

      const rampTotal = rampSteps.length * 90

      timers.push(
        setTimeout(() => {
          setCompletionState(prev => ({ ...prev, [attachmentId]: 'barExit' }))
        }, rampTotal + 120),
      )

      timers.push(
        setTimeout(() => {
          setCompletionState(prev => ({ ...prev, [attachmentId]: 'tickReveal' }))
        }, rampTotal + 360),
      )

      timers.push(
        setTimeout(() => {
          clearCompletionTimers(attachmentId)
          setCompletionProgress(prev => {
            const next = { ...prev }
            delete next[attachmentId]
            return next
          })
          setCompletionState(prev => {
            const next = { ...prev }
            delete next[attachmentId]
            return next
          })
        }, rampTotal + 800),
      )
    })
  }, [validAttachments, dummyProgress])

  useEffect(() => {
    return () => {
      Object.values(completionTimersRef.current).forEach(timerList => {
        timerList.forEach(timerId => clearTimeout(timerId))
      })
      completionTimersRef.current = {}
    }
  }, [])

  const handleDeleteAttachment = attachmentId => {
    track('delete_attachment')
    clearCompletionTimers(attachmentId)
    dispatch(aICreatorDeleteAttachmentAction(attachmentId))
  }

  return (
    <div className={classes.container}>
      {validAttachments.length > 0 && null}

      {validAttachments.map(attachment => {
        const attachmentId = String(attachment.attachment_id)
        const rawAttachmentId = attachment.attachment_id
        const hasError =
          attachment.upload_status === 'partial_failure' ||
          attachment.upload_status === 'failed' ||
          (attachment.error_details && attachment.error_details.length > 0)
        const hasCompletionProgress = completionProgress[attachmentId] != null
        const isProcessing =
          attachment.succeeded === false &&
          attachment.upload_status !== 'partial_failure' &&
          attachment.upload_status !== 'failed' &&
          !(attachment.error_details && attachment.error_details.length > 0)
        const shouldShowProgress = isProcessing || hasCompletionProgress
        const progressValue = hasCompletionProgress
          ? completionProgress[attachmentId]
          : dummyProgress[attachmentId] || 0
        const state = completionState[attachmentId]
        const shouldShowProgressTick =
          state === 'barExit' || state === 'tickReveal'

        return (
          <div key={attachmentId} className={classes.attachmentItem}>
            <div className={classes.attachmentRow}>
              <Icon
                icon={
                  hasError
                    ? 'ph:warning-circle'
                    : attachment.attachment_type === 'youtube'
                    ? 'ph:video-camera'
                    : 'ph:file-text'
                }
                className={clsx(classes.fileIcon, {
                  [classes.fileIconError]: hasError || attachment.succeeded === false,
                  [classes.fileIconSuccess]: !hasError && attachment.succeeded === true,
                })}
              />
              <Typography className={classes.fileName}>
                {attachment.attachment_type === 'youtube' ||
                attachment.attachment_type === 'web'
                  ? attachment.file_url ||
                    attachment.source_url ||
                    attachment.original_file_name ||
                    attachment.file_name
                  : attachment.file_name || attachment.original_file_name}
              </Typography>
              <div className={classes.flexGrow} />
              <div className={classes.rightSection}>
                <div className={classes.progressArea}>
                  {shouldShowProgress && (
                    <div
                      className={clsx(classes.progressTrack, {
                        [classes.progressTrackExit]:
                          state === 'barExit' || state === 'tickReveal',
                      })}
                    >
                      <div
                        className={classes.progressFill}
                        style={{ width: `${Math.min(progressValue, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                <div className={classes.actionSlot}>
                  {shouldShowProgress ? (
                    shouldShowProgressTick ? (
                      <Icon
                        icon="charm:tick"
                        className={clsx(classes.tickIcon, classes.progressTickReveal)}
                      />
                    ) : (
                      <Typography className={classes.progressText}>
                        {Math.round(progressValue)}%
                      </Typography>
                    )
                  ) : (
                    <div
                      onMouseEnter={() => setHoveredIconId(attachmentId)}
                      onMouseLeave={() => setHoveredIconId(null)}
                    >
                      {hoveredIconId === attachmentId || hasError ? (
                        <Icon
                          icon="iconamoon:close-light"
                          className={classes.closeIcon}
                          onClick={() => handleDeleteAttachment(rawAttachmentId)}
                        />
                      ) : (
                        <Icon icon="charm:tick" className={classes.tickIcon} />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isProcessing && attachment.attachment_type === 'youtube' && (
              <Typography className={classes.youtubeHint}>
                We are taking screenshots of your video, this may take a while.
              </Typography>
            )}
          </div>
        )
      })}

      {validAttachments.length > 0 && (
        <div className={classes.documentFollowingContainer}>
          <Typography fontSize={14} fontWeight={500}>
            Source Following
          </Typography>
          <Box sx={{ width: '100%', padding: '0 8px' }}>
            <Slider
              value={
                documentFollowing === 'reference'
                  ? 0
                  : documentFollowing === 'follow'
                  ? 50
                  : 100
              }
              onChange={(event, newValue) => {
                if (newValue <= 25) {
                  onDocumentFollowingChange('reference')
                } else if (newValue <= 75) {
                  onDocumentFollowingChange('follow')
                } else {
                  onDocumentFollowingChange('preserve')
                }
              }}
              step={null}
              marks={[
                {
                  value: 0,
                  label: (
                    <Tooltip title="Follow the instructions exactly and ensure all content is grounded in the documents.">
                      <span>Reference</span>
                    </Tooltip>
                  ),
                },
                {
                  value: 50,
                  label: (
                    <Tooltip title="Follow the document structure strictly, allowing for summarization and rephrasing of the content.">
                      <span>Follow</span>
                    </Tooltip>
                  ),
                },
                {
                  value: 100,
                  label: (
                    <Tooltip title="Stick closely to the original text and structure, with minimal modifications.">
                      <span>Preserve</span>
                    </Tooltip>
                  ),
                },
              ]}
              sx={{
                '& .MuiSlider-mark': {
                  backgroundColor: 'transparent',
                  display: 'none',
                  height: 8,
                  '&.MuiSlider-markActive': {
                    opacity: 1,
                    backgroundColor: '#015ffb',
                  },
                },
                '& .MuiSlider-markLabel': {
                  fontSize: '12px',
                  marginTop: '8px',
                  color: '#637381',
                  fontWeight: 500,
                  '&[data-index="0"]': {
                    marginLeft: '20px',
                  },
                  '&[data-index="1"]': {
                    marginLeft: '0px',
                  },
                  '&[data-index="2"]': {
                    marginLeft: '-20px',
                  },
                  '@media (min-width: 600px)': {
                    '&[data-index="0"]': {
                      marginLeft: '30px',
                    },
                    '&[data-index="1"]': {
                      marginLeft: '3px',
                    },
                    '&[data-index="2"]': {
                      marginLeft: '-25px',
                    },
                  },
                },
                '& .MuiSlider-thumb': {
                  height: 20,
                  width: 20,
                  backgroundColor: '#015ffb',
                  border: '2px solid currentColor',
                  '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                    boxShadow: 'inherit',
                  },
                  '&:before': {
                    display: 'none',
                  },
                },
                '& .MuiSlider-track': {
                  background: '#015ffb',
                  border: 'none',
                  height: 8,
                  borderRadius: 4,
                },
                '& .MuiSlider-rail': {
                  backgroundColor: '#4D8FFC',
                  height: 8,
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        </div>
      )}
    </div>
  )
}

export default withStyles(styles)(CourseCreationAttachmentView)
